import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  GRID_CHECKBOX_SELECTION_FIELD,
  GRID_TREE_DATA_GROUPING_FIELD,
  GridEventListener,
  GridPinnedColumnFields,
  GridSortModel,
  GridValidRowModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium';

import { CHECKBOX_HIDDEN_ROW_CLASS, SYSTEM_COLUMN_FIELDS } from './GeneratikTable.constants';
import {
  GeneratikRowClassNameParams,
  GeneratikTableColumn,
  GeneratikTableProps,
} from './GeneratikTable.types';
import { dedupeRowsById } from './GeneratikTable.utils';
import { GeneratikTableContextValue, GeneratikTableProvider } from './GeneratikTableContext';
import GeneratikTableDefaultDisplay from './GeneratikTableDefaultDisplay';
import GeneratikTableDialogDisplay from './GeneratikTableDialogDisplay';
import { FilterValues } from './GeneratikTableFilterPanel.types';
import useTableZoom from './contextMenu/useTableZoom';
import { buildColumnWidthKey } from './generatikTableStore';
import useAutosizeColumnsOnMount from './hooks/useAutosizeColumnsOnMount';
import useGeneratikTableColumns from './hooks/useGeneratikTableColumns';
import useGeneratikTableSlots from './hooks/useGeneratikTableSlots';
import useMountedGridApi from './hooks/useMountedGridApi';
import useNotifyTableConfigChange from './hooks/useNotifyTableConfigChange';
import usePreserveHorizontalScroll from './hooks/usePreserveHorizontalScroll';
import useProcessedColumns from './hooks/useProcessedColumns';
import useScrollResetOnQueryChange from './hooks/useScrollResetOnQueryChange';
import useScrollTimelineToToday from './hooks/useScrollTimelineToToday';
import useScrollToRow from './hooks/useScrollToRow';
import useSortModelActions from './hooks/useSortModelActions';
import useSystemColumnLayout from './hooks/useSystemColumnLayout';
import useTableGrouping from './hooks/useTableGrouping';
import useTableRowInteractions from './hooks/useTableRowInteractions';
import useTableSelection from './hooks/useTableSelection';
import useTableSorting from './hooks/useTableSorting';
import useTableTotals from './hooks/useTableTotals';
import useTimelineColumns from './hooks/useTimelineColumns';
import useTimelineVariant from './hooks/useTimelineVariant';
import useTreeExpansion from './hooks/useTreeExpansion';
import useVisibleRowIds from './hooks/useVisibleRowIds';
import type { ResizableGridColDef } from './persistence/headersApiAdapter';
import useTableHeadersPersistence from './persistence/useTableHeadersPersistence';
import { dropsOnlyUnrenderedLevels } from './sortModel';
import { timelineWindowEnd, timelineWindowStart, toDayjs } from './timeline/timeline.utils';

export { CellWithLabelRenderer, ProgressBarRenderer } from './GeneratikTable.cells';
export type { CellWithLabelProps, ProgressBarProps } from './GeneratikTable.cells';

const NO_FIELDS: ReadonlySet<string> = new Set();

const GeneratikTable = <R extends GridValidRowModel = GridValidRowModel>({
  columns,
  rows,
  scrollToRow,
  rowCount,
  loading = false,
  checkboxSelection,
  checkboxSize = 'small',
  radioSelection,
  disableRowSelectionOnClick = true,
  isRowSelectable,
  isRowCheckboxHidden,
  isRowSelectionIndependent,
  disabledCheckboxTooltip,
  onDisabledCheckboxClick,
  getRowId,
  paginationMode = 'client',
  paginationModel = { page: 0, pageSize: 25 },
  pageSizeOptions = [10, 25, 50, 100],
  onPaginationModelChange,
  sortingMode = 'client',
  sortModel,
  onSortModelChange,
  filterMode = 'client',
  filterModel,
  onFilterModelChange,
  columnVisibilityModel,
  campaignColumnVisibility,
  onColumnVisibilityModelChange,
  onRowSelectionModelChange,
  onSelectedRowsChange,
  onVisibleRowIdsChange,
  getRowClassName: getExternalRowClassName,
  onRowClick,
  onRowDoubleClick,
  enableContextMenu = true,
  columnFilterMap,
  columnRawFieldMap,
  getCellClassName: externalGetCellClassName,
  hideToolbar = true,
  hidePagination = false,
  hideColumnMenu = false,
  CustomToolbar,
  columnReordering = true,
  pinnedColumns,
  density = 'standard',
  height = 'fill',
  sx,
  title,
  titleIcon,
  hideHeader = false,
  hideCount = false,
  leftButtons,
  rightButtons,
  searchValue = '',
  onSearchChange,
  searchPlaceholder,
  showSearch = false,
  showColumns = true,
  disableColumnsButton = false,
  onColumnVisibilityChange,
  sortOptions,
  groupOptions,
  groupByField,
  onGroupChange,
  groupRowCountLabel,
  viewMode = 'grid',
  onViewModeChange,
  hideCustomToolbar = false,
  filterFields = [],
  filterValues = {},
  onFilterValuesChange,
  activeFilters = [],
  onFilterRemove,
  onFilterApply,
  onFilterReset,
  quickFilters,
  treeData,
  getTreeDataPath,
  selectDescendants = false,
  groupValueGetter,
  defaultGroupingExpansionDepth,
  getRowExpandedByDefault,
  forceExpandedTreeRowIds,
  collapsedGroupIds,
  onCollapsedGroupIdsChange,
  autosizeOnMount = true,
  autosizeOptions,
  calculatePageTotal,
  calculateSelectedTotal,
  spacesTotal,
  productionTotal,
  grandTotal,
  currency,
  emptyStateMessage,
  isError = false,
  onRetry,
  predefinedFilters,
  requiresCampaignPeriod = false,
  headersRequest,
  onTableConfigChange,
  onColumnOrderChange,
  getRowCanExpand,
  renderDetailPanel,
  detailPanelHeight = 'auto',
  detailPanelExpandedRowIds,
  onDetailPanelExpandedRowIdsChange,
  rowHeight,
  renderListViewRow,
  getGroupItemPrice,
  groupPriceForPeriod,
  isModal,
  dialogTestId,
  modalActions,
  stackedModalFooter,
  drawerOpen,
  processRowUpdate,
  singleClickEdit = false,
  hasBackButton = false,

  persistSelectedRows,
  externalSelectionModel,
  scrollToRowId,
  clearSelectionKey,
  renderGroupingCell,
  groupingColumnWidth,
  noTopPadding,
  noPadding,
  seamless,
  plainToolbar,
  toolbarStartContent,
  hideVerticalScrollbar,
  getRowBookings,
  onBarClick,
  timelineMinDate,
  timelineMaxDate,
  timelineFocusDate,
  timelineHighlightRange,
  variant: variantProp,
  onVariantChange,
  timelineGranularity: granularityProp,
  onTimelineGranularityChange,
  timelineAxis = 'calendar',
  timelineAxisWindow,
  timelineAxisWindowMode,
  timelineAxisProtectRange,
  timelineDataPending,
  onHeadersLoaded,
}: GeneratikTableProps<R>) => {
  const fillHeight = height === 'fill';
  const { t } = useTranslation();
  const apiRef = useGridApiRef();
  const [gridRootElement, setGridRootElement] = useState<HTMLDivElement | null>(null);
  const gridApi = useMountedGridApi(apiRef, gridRootElement);

  const widthKey = useMemo(
    () =>
      headersRequest
        ? buildColumnWidthKey(headersRequest.pageType, headersRequest.resourceId)
        : null,
    [headersRequest],
  );
  const { level: zoomLevel, setLevel: onZoomChange } = useTableZoom(widthKey);

  const {
    variant,
    timelineGranularity,
    handleVariantChange,
    handleTimelineGranularityChange,
    hasTimelineColumns,
    isTimelineActive,
    normalizedTimelineMin,
    normalizedTimelineMax,
    normalizedHighlightRange,
  } = useTimelineVariant<R>({
    columns,
    getRowBookings,
    variant: variantProp,
    onVariantChange,
    timelineGranularity: granularityProp,
    onTimelineGranularityChange,
    timelineMinDate,
    timelineMaxDate,
    timelineHighlightRange,
  });

  // Normalized separately, like normalizedTimelineMin/Max, so useTimelineColumns's memo depends
  // on the scalar dates rather than this object's identity, which is a fresh literal on every
  // render for every caller that inlines it.
  const normalizedAxisWindowStart = useMemo(
    () => timelineWindowStart(timelineAxisWindow?.start),
    [timelineAxisWindow?.start],
  );
  const normalizedAxisWindowEnd = useMemo(
    () => timelineWindowEnd(timelineAxisWindow?.end),
    [timelineAxisWindow?.end],
  );
  const normalizedAxisProtectStart = useMemo(
    () => toDayjs(timelineAxisProtectRange?.start),
    [timelineAxisProtectRange?.start],
  );
  const normalizedAxisProtectEnd = useMemo(
    () => toDayjs(timelineAxisProtectRange?.end),
    [timelineAxisProtectRange?.end],
  );

  const filterButtonRef = useRef<HTMLButtonElement>(null);

  const defaultGetRowId = useCallback(
    (row: GridValidRowModel) => (row as { id: string | number }).id,
    [],
  );
  const resolveRowIdForGrouping = useMemo(
    () => (getRowId ?? defaultGetRowId) as (row: R) => string | number,
    [getRowId, defaultGetRowId],
  );

  const safeRows = useMemo(
    () => dedupeRowsById(rows, resolveRowIdForGrouping),
    [rows, resolveRowIdForGrouping],
  );

  const isChildRow = useMemo(() => {
    if (!getTreeDataPath) return undefined;
    return (row: R) => getTreeDataPath(row).length > 1;
  }, [getTreeDataPath]);

  const { treeDataEnabled, effectiveGetTreeDataPath, groupingColDef } = useTableGrouping<R>({
    groupByField,
    treeData,
    columns,
    rows: safeRows,
    getRowId: resolveRowIdForGrouping,
    userGetTreeDataPath: getTreeDataPath as ((row: R) => Array<string | number>) | undefined,
    groupValueGetter,
    renderGroupingCell,
    groupingColumnWidth,
    onGroupChange,
  });

  const {
    selectionModel,
    visibleSelectionModel,
    handleRowSelectionChange,
    toggleRowSelection,
    resolveRowId,
  } = useTableSelection<R>({
    rows: safeRows,
    apiRef,
    getRowId,
    radioSelection,
    paginationMode,
    selectDescendants,
    treeData: treeDataEnabled,
    getTreeDataPath: effectiveGetTreeDataPath as ((row: R) => string[]) | undefined,
    externalSelectionModel,
    clearSelectionKey,
    groupByField,
    isRowSelectable,
    isRowSelectionIndependent,
    persistSelectedRows,
    headersRequest,
    onRowSelectionModelChange,
    onSelectedRowsChange,
  });

  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [focusFieldId, setFocusFieldId] = useState<string | null>(null);
  const baseColumns = useGeneratikTableColumns(columns, { hideColumnMenu });

  const { effectiveSortModel, effectiveOnSortModelChange } = useTableSorting({
    groupByField,
    sortModel,
    onSortModelChange,
  });

  const sortActions = useSortModelActions({
    sortModel: effectiveSortModel,
    onSortModelChange: effectiveOnSortModelChange,
  });

  // Persisted-width columns carry their persisted width as minWidth (that is
  // what keeps user widths exact when the grid overflows), which would also be
  // the resize clamp's lower bound — freezing every shrink. MUI captures the
  // exact colDef object for the drag right after publishing columnResizeStart,
  // so lowering minWidth on that object here is what lets the drag go down to
  // the column's natural floor; the post-resize snapshot triggers a columns
  // hydration that restores the persisted-width semantics.
  const handleColumnResizeStart = useCallback<GridEventListener<'columnResizeStart'>>(
    ({ field }) => {
      try {
        const col = apiRef.current?.getColumn(field) as ResizableGridColDef | undefined;
        const floor = col?.generatikResizeFloor;
        if (col && typeof floor === 'number' && (col.minWidth ?? 0) > floor) {
          col.minWidth = floor;
        }
      } catch {
        // unknown column or API drift — resizing keeps the persisted lower bound
      }
    },
    [apiRef],
  );

  // A plain header click adds a sort level rather than replacing the sort, so
  // multi-column sorting needs no modifier key. The grid's own sort handler is
  // registered behind a `defaultMuiPrevented` guard and prop handlers run first,
  // so cancelling here suppresses its replace-the-model behaviour. Modifier
  // clicks fall through to the grid, which appends the same way.
  const handleColumnHeaderClick = useCallback<GridEventListener<'columnHeaderClick'>>(
    (params, event) => {
      if (event.shiftKey || event.metaKey || event.ctrlKey) return;
      if (!params.colDef.sortable) return;
      event.defaultMuiPrevented = true;
      sortActions.cycle(params.field, { append: true });
    },
    [sortActions],
  );

  const { processedColumns, autoHiddenColumns } = useProcessedColumns<R>({
    baseColumns,
    columns,
    rows: safeRows,
    sortOptions,
    groupByField,
    treeDataEnabled,
    effectiveGetTreeDataPath,
  });

  const handleFilterClick = useCallback(
    (eventOrFieldId?: React.MouseEvent<HTMLElement> | string) => {
      if (typeof eventOrFieldId === 'string') {
        setFocusFieldId(eventOrFieldId);
      } else {
        setFocusFieldId(null);
      }
      setFilterAnchorEl(filterButtonRef.current);
    },
    [],
  );

  const { CheckboxSlot, RadioSlot, NoRowsOverlay, ToolbarComponent } = useGeneratikTableSlots({
    checkboxSize,
    disabledCheckboxTooltip,
    onDisabledCheckboxClick,
    handleFilterClick,
    requiresCampaignPeriod,
    filterValues,
    emptyStateMessage,
    isError,
    onRetry,
    hideToolbar,
    CustomToolbar,
  });

  const rowClassNameWithCheckbox = useCallback(
    (params: GeneratikRowClassNameParams<R>) =>
      [
        getExternalRowClassName?.(params),
        params.row && isRowCheckboxHidden?.(params.row) ? CHECKBOX_HIDDEN_ROW_CLASS : '',
      ]
        .filter(Boolean)
        .join(' '),
    [getExternalRowClassName, isRowCheckboxHidden],
  );

  const { handleRowClick, getCellClassName, handleRowDoubleClick, getRowClassName } =
    useTableRowInteractions<R>({
      apiRef,
      onRowClick,
      onRowDoubleClick,
      treeDataEnabled,
      groupByField,
      externalGetCellClassName,
      getRowClassNameProp: rowClassNameWithCheckbox,
    });

  const handlePageChange = useCallback(
    (newPage: number) => {
      onPaginationModelChange?.({ ...paginationModel, page: newPage });
    },
    [onPaginationModelChange, paginationModel],
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      onPaginationModelChange?.({ page: 0, pageSize: newPageSize });
    },
    [onPaginationModelChange],
  );

  // Keyed only by the grouping so a groupBy change rebuilds the grid. Row
  // changes must NOT be part of the key: a row action that refreshes the same
  // query has to update the grid in place, or scroll, expansion, focus and
  // detail panels reset on every action (AAD-3221). Scroll-to-top for a page,
  // sort, filter or search change is handled by useScrollResetOnQueryChange.
  const dataGridKey = useMemo(() => `groupBy:${groupByField ?? 'none'}`, [groupByField]);

  useEffect(() => {
    if (!gridApi) return undefined;
    return gridApi.subscribeEvent('columnResizeStart', handleColumnResizeStart);
  }, [gridApi, handleColumnResizeStart]);

  const handleFilterClose = useCallback(() => {
    setFilterAnchorEl(null);
    setFocusFieldId(null);
  }, []);

  const handleFilterApplyInternal = useCallback(
    (values?: FilterValues) => {
      onFilterApply?.(values);
    },
    [onFilterApply],
  );

  const tableHeight = useMemo(() => {
    if (height === 'auto') return 'auto';
    if (fillHeight) return '100%';

    if (typeof height === 'number') {
      return hidePagination ? height : height - 49;
    }
    return height;
  }, [height, hidePagination, fillHeight]);

  const hasRightButtons =
    (Array.isArray(rightButtons) ? rightButtons.length > 0 : !!rightButtons) ||
    !!onColumnVisibilityModelChange;
  const showCustomToolbar =
    !hideCustomToolbar && (title || leftButtons?.length || hasRightButtons || toolbarStartContent);

  const totalCount = rowCount ?? safeRows.length;

  const [userPinnedColumns, setUserPinnedColumns] = useState<GridPinnedColumnFields | undefined>(
    pinnedColumns,
  );

  useEffect(() => {
    setUserPinnedColumns(pinnedColumns);
  }, [pinnedColumns]);

  const { effectivePinnedColumns, effectiveColumnVisibilityModel } = useSystemColumnLayout<R>({
    columns,
    autoHiddenColumns,
    userPinnedColumns,
    columnVisibilityModel,
    groupByField,
  });

  const {
    orderedColumns,
    isHeadersLoading,
    getColumnOrder,
    handleColumnOrderChange: persistedColumnOrderChange,
    handleVisibilityChange: persistedVisibilityChange,
    handleColumnWidthChange: persistedColumnWidthChange,
  } = useTableHeadersPersistence({
    headersRequest,
    processedColumns,
    effectiveColumnVisibilityModel,
    apiRef,
    onColumnVisibilityModelChange,
    onColumnOrderChange,
    groupByField,
    onGroupByChange: onGroupChange,
    pinnedColumns: userPinnedColumns,
    onPinnedColumnsChange: setUserPinnedColumns,
    mandatoryPinnedColumns: pinnedColumns,
  });

  const {
    columns: timelineColumns,
    pinnedFields: timelinePinnedFields,
    range: timelineRange,
    dateToOffset: timelineDateToOffset,
    axis: effectiveTimelineAxis,
  } = useTimelineColumns({
    enabled: isTimelineActive,
    userColumns: orderedColumns as GeneratikTableColumn<R>[],
    rows: safeRows,
    granularity: timelineGranularity,
    axis: timelineAxis,
    getRowBookings,
    onBarClick,
    timelineMinDate: normalizedTimelineMin,
    timelineMaxDate: normalizedTimelineMax,
    highlightRange: normalizedHighlightRange,
    axisWindowStart: normalizedAxisWindowStart,
    axisWindowEnd: normalizedAxisWindowEnd,
    axisWindowMode: timelineAxisWindowMode,
    axisProtectStart: normalizedAxisProtectStart,
    axisProtectEnd: normalizedAxisProtectEnd,
    dataPending: timelineDataPending,
  });

  const displayColumns = isTimelineActive ? timelineColumns : orderedColumns;

  // The rail hands the grid only its `timeline` columns, and MUI unsorts any column that leaves
  // the grid, reporting that through onSortModelChange exactly like a user edit. Those levels are
  // still real page sorts, so the grid-originated callback drops that one edit and passes on
  // everything else; the table's own sort actions never go through it.
  const railHiddenFields = useMemo<ReadonlySet<string>>(() => {
    if (!isTimelineActive) return NO_FIELDS;
    const rendered = new Set(timelineColumns.map((col) => col.field));
    return new Set(
      processedColumns.map((col) => col.field).filter((field) => !rendered.has(field)),
    );
  }, [isTimelineActive, timelineColumns, processedColumns]);

  const handleGridSortModelChange = useCallback(
    (model: GridSortModel) => {
      if (dropsOnlyUnrenderedLevels(effectiveSortModel, model, railHiddenFields)) return;
      effectiveOnSortModelChange(model);
    },
    [effectiveSortModel, effectiveOnSortModelChange, railHiddenFields],
  );

  const finalPinnedColumns = useMemo(() => {
    if (!isTimelineActive) return effectivePinnedColumns;
    const hasActions =
      columns.some((col) => col.field === 'actions') && !autoHiddenColumns.has('actions');
    return {
      left: [
        GRID_CHECKBOX_SELECTION_FIELD,
        ...(autoHiddenColumns.has(GRID_TREE_DATA_GROUPING_FIELD)
          ? []
          : [GRID_TREE_DATA_GROUPING_FIELD]),
        ...(hasActions ? ['actions'] : []),
        ...timelinePinnedFields,
      ],
      right: undefined,
    };
  }, [isTimelineActive, effectivePinnedColumns, columns, autoHiddenColumns, timelinePinnedFields]);

  const { selectedRows, pageTotal, pageSelected } = useTableTotals<R>({
    rows: safeRows,
    paginationMode,
    paginationModel,
    selectionModel,
    resolveRowId,
    calculatePageTotal,
    calculateSelectedTotal,
    getGroupItemPrice,
    persistSelectedRows,
    onSelectedRowsChange,
  });

  useAutosizeColumnsOnMount<R>({
    autosizeOnMount,
    rows: safeRows,
    columns,
    apiRef,
    autosizeOptions,
    headersRequest,
    columnVisibilityModel: effectiveColumnVisibilityModel,
  });

  useScrollToRow<R>({
    scrollToRow,
    scrollToRowId,
    rows,
    apiRef,
    isHeadersLoading,
  });

  useEffect(() => {
    if (!requiresCampaignPeriod) return;

    const dateRange = filterValues?.dateRange as { start?: string; end?: string } | undefined;
    const hasStartDate = dateRange?.start || filterValues?.start || filterValues?.startDate;
    const hasEndDate = dateRange?.end || filterValues?.end || filterValues?.endDate;

    if ((!hasStartDate || !hasEndDate) && selectionModel.length > 0) {
      handleRowSelectionChange([]);
    }
  }, [requiresCampaignPeriod, filterValues, selectionModel.length, handleRowSelectionChange]);

  useEffect(() => {
    const handleOpenFilter = (e: Event) => {
      const customEvent = e as CustomEvent<{ fieldId?: string }>;
      handleFilterClick(customEvent.detail?.fieldId);
    };
    window.addEventListener('generatik-table-open-filter', handleOpenFilter);
    return () => window.removeEventListener('generatik-table-open-filter', handleOpenFilter);
  }, [handleFilterClick]);

  const { isGroupExpandedByDefault: effectiveIsGroupExpandedByDefault } = useTreeExpansion<R>({
    treeDataEnabled,
    gridApi,
    dataGridKey,
    rows: safeRows,
    resolveRowId,
    forceExpandedTreeRowIds,
    defaultGroupingExpansionDepth,
    getRowExpandedByDefault,
    persistedCollapsedRowIds: collapsedGroupIds,
    onCollapsedRowIdsChange: onCollapsedGroupIdsChange,
  });

  usePreserveHorizontalScroll(gridApi);
  useScrollResetOnQueryChange({
    apiRef,
    enabled: paginationMode === 'server',
    paginationModel,
    sortModel: effectiveSortModel,
    filterModel,
    filterValues,
    searchValue,
  });
  useVisibleRowIds(apiRef, gridApi, onVisibleRowIdsChange);
  useScrollTimelineToToday(
    apiRef,
    timelineRange,
    timelineDateToOffset,
    effectiveTimelineAxis === 'bookings' ? 'bookings' : timelineGranularity,
    isTimelineActive,
    toDayjs(timelineFocusDate),
  );

  const prevHeadersLoadingRef = useRef(isHeadersLoading);
  useEffect(() => {
    if (prevHeadersLoadingRef.current && !isHeadersLoading) {
      onHeadersLoaded?.();
    }
    prevHeadersLoadingRef.current = isHeadersLoading;
  }, [isHeadersLoading, onHeadersLoaded]);

  const tableConfig = useMemo(() => {
    const allColumnFields = processedColumns
      .map((col) => col.field)
      .filter((field) => !SYSTEM_COLUMN_FIELDS.has(field));

    const visibleColumns = allColumnFields.filter(
      (field) => effectiveColumnVisibilityModel[field] !== false,
    );

    const persistedColumnOrder = getColumnOrder();
    const columnOrder = [
      ...persistedColumnOrder.filter((field) => allColumnFields.includes(field)),
      ...allColumnFields.filter((field) => !persistedColumnOrder.includes(field)),
    ];

    return { visibleColumns, columnOrder };
  }, [processedColumns, effectiveColumnVisibilityModel, getColumnOrder]);

  useNotifyTableConfigChange({
    config: tableConfig,
    enabled: Boolean(onTableConfigChange && headersRequest),
    onChange: onTableConfigChange,
  });

  const contextValue = useMemo(
    () => ({
      checkboxSelection,
      radioSelection,
      columns,
      rows: safeRows,
      rowCount,
      loading: loading,
      disableRowSelectionOnClick,
      isRowSelectable,
      disabledCheckboxTooltip,
      onDisabledCheckboxClick,
      getRowId,
      paginationMode,
      paginationModel,
      pageSizeOptions,
      onPaginationModelChange,
      sortingMode: groupByField ? ('client' as const) : sortingMode,
      sortModel: effectiveSortModel,
      onSortModelChange: handleGridSortModelChange,
      filterMode,
      filterModel,
      onFilterModelChange,
      columnVisibilityModel,
      campaignColumnVisibility,
      onColumnVisibilityModelChange: persistedVisibilityChange,
      onRowSelectionModelChange,
      onSelectedRowsChange,
      onRowClick,
      onRowDoubleClick,
      hideToolbar,
      hidePagination,
      hideColumnMenu,
      CustomToolbar,
      columnReordering,
      pinnedColumns: finalPinnedColumns,
      onPinnedColumnsChange: setUserPinnedColumns,
      density,
      height,
      sx,
      title,
      titleIcon,
      hasBackButton,
      hideHeader,
      hideCount,
      leftButtons,
      rightButtons,
      searchValue,
      onSearchChange,
      searchPlaceholder,
      showSearch,
      showColumns,
      disableColumnsButton: disableColumnsButton || viewMode === 'list',
      disableGroupButton: viewMode === 'list' && !renderListViewRow,
      onColumnVisibilityChange,
      sortOptions,
      sortActions,
      onColumnHeaderClick: handleColumnHeaderClick,
      groupOptions,
      groupByField,
      onGroupChange,
      viewMode,
      onViewModeChange,
      hideCustomToolbar,
      filterFields,
      filterValues,
      onFilterValuesChange,
      enableContextMenu,
      columnFilterMap,
      columnRawFieldMap,
      zoomLevel,
      onZoomChange,
      activeFilters,
      onFilterRemove,
      onFilterApply,
      onFilterReset,
      quickFilters,
      treeData,
      treeDataEnabled,
      getTreeDataPath,
      effectiveGetTreeDataPath,
      isChildRow,
      isGroupExpandedByDefault: treeDataEnabled ? effectiveIsGroupExpandedByDefault : undefined,
      autosizeOnMount,
      autosizeOptions,
      calculatePageTotal,
      calculateSelectedTotal,
      spacesTotal,
      productionTotal,
      grandTotal,
      currency,
      emptyStateMessage,
      isError,
      onRetry,
      predefinedFilters,
      requiresCampaignPeriod,
      headersRequest,
      isHeadersLoading,
      onTableConfigChange,
      onColumnOrderChange,
      getRowCanExpand,
      renderDetailPanel,
      detailPanelHeight,
      detailPanelExpandedRowIds,
      onDetailPanelExpandedRowIdsChange,
      rowHeight,
      renderListViewRow,
      getGroupItemPrice,
      groupPriceForPeriod,
      groupRowCountLabel,
      modalActions,
      stackedModalFooter,
      dialogTestId,
      defaultGroupingExpansionDepth,
      drawerOpen,
      processRowUpdate,
      singleClickEdit,

      // Computed values
      fillHeight,
      showCustomToolbar: !!showCustomToolbar,
      totalCount,
      processedColumns: displayColumns,

      // Timeline variant
      variant,
      timelineGranularity,
      hasTimelineColumns,
      isTimelineActive,
      effectiveTimelineAxis,
      onVariantChange: handleVariantChange,
      onTimelineGranularityChange: handleTimelineGranularityChange,
      defaultGetRowId,
      selectionModel: visibleSelectionModel,
      dataGridKey,
      effectiveColumnVisibilityModel,
      groupingColDef,
      tableHeight,
      pageTotal,
      pageSelected,
      selectedRows,

      // Handlers
      handleFilterClick,
      handleRowSelectionChange,
      toggleRowSelection,
      handleRowClick,
      handleRowDoubleClick,
      getCellClassName,
      getRowClassName,
      handleColumnOrderChange: persistedColumnOrderChange,
      handleColumnWidthChange: persistedColumnWidthChange,
      handlePageChange,
      handlePageSizeChange,
      handleFilterClose,
      handleFilterApplyInternal,

      // Refs
      apiRef,
      gridRootRef: setGridRootElement,
      filterButtonRef,
      filterAnchorEl,
      focusFieldId,

      noTopPadding,
      noPadding,
      seamless,
      plainToolbar,
      toolbarStartContent,
      hideVerticalScrollbar,

      // Slots
      ToolbarComponent,
      NoRowsOverlay,
      CheckboxSlot,
      RadioSlot,

      // i18n
      t,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      checkboxSelection,
      radioSelection,
      columns,
      safeRows,
      rowCount,
      loading,
      isHeadersLoading,
      disableRowSelectionOnClick,
      isRowSelectable,
      getRowId,
      paginationMode,
      paginationModel,
      pageSizeOptions,
      onPaginationModelChange,
      sortingMode,
      sortModel,
      effectiveSortModel,
      handleGridSortModelChange,
      onSortModelChange,
      filterMode,
      filterModel,
      onFilterModelChange,
      columnVisibilityModel,
      campaignColumnVisibility,
      persistedVisibilityChange,
      hideToolbar,
      hidePagination,
      hideColumnMenu,
      columnReordering,
      finalPinnedColumns,
      density,
      height,
      sx,
      title,
      titleIcon,
      hasBackButton,

      hideHeader,
      hideCount,
      leftButtons,
      rightButtons,
      searchValue,
      onSearchChange,
      showSearch,
      showColumns,
      disableColumnsButton,
      onColumnVisibilityChange,
      sortOptions,
      sortActions,
      handleColumnHeaderClick,
      groupOptions,
      groupByField,
      onGroupChange,
      viewMode,
      onViewModeChange,
      hideCustomToolbar,
      filterFields,
      filterValues,
      onFilterValuesChange,
      enableContextMenu,
      columnFilterMap,
      columnRawFieldMap,
      zoomLevel,
      onZoomChange,
      activeFilters,
      onFilterRemove,
      onFilterApply,
      onFilterReset,
      quickFilters,
      treeData,
      treeDataEnabled,
      getTreeDataPath,
      effectiveGetTreeDataPath,
      isChildRow,
      defaultGroupingExpansionDepth,
      effectiveIsGroupExpandedByDefault,
      currency,
      emptyStateMessage,
      isError,
      onRetry,
      predefinedFilters,
      headersRequest,
      onTableConfigChange,
      getRowCanExpand,
      renderDetailPanel,
      detailPanelHeight,
      detailPanelExpandedRowIds,
      onDetailPanelExpandedRowIdsChange,
      rowHeight,
      renderListViewRow,
      getGroupItemPrice,
      groupPriceForPeriod,
      groupRowCountLabel,
      modalActions,
      stackedModalFooter,
      processRowUpdate,
      singleClickEdit,
      fillHeight,
      showCustomToolbar,
      totalCount,
      displayColumns,
      variant,
      timelineGranularity,
      hasTimelineColumns,
      isTimelineActive,
      effectiveTimelineAxis,
      handleVariantChange,
      handleTimelineGranularityChange,
      defaultGetRowId,
      visibleSelectionModel,
      dataGridKey,
      effectiveColumnVisibilityModel,
      groupingColDef,
      tableHeight,
      pageTotal,
      pageSelected,
      selectedRows,
      handleFilterClick,
      handleRowSelectionChange,
      toggleRowSelection,
      handleRowClick,
      handleRowDoubleClick,
      getCellClassName,
      getRowClassName,
      persistedColumnOrderChange,
      persistedColumnWidthChange,
      handlePageChange,
      handlePageSizeChange,
      handleFilterClose,
      handleFilterApplyInternal,
      apiRef,
      filterButtonRef,
      filterAnchorEl,
      focusFieldId,
      ToolbarComponent,
      NoRowsOverlay,
      CheckboxSlot,
      RadioSlot,
      t,
    ],
  );

  return (
    <GeneratikTableProvider value={contextValue as unknown as GeneratikTableContextValue}>
      {isModal ? <GeneratikTableDialogDisplay /> : <GeneratikTableDefaultDisplay />}
    </GeneratikTableProvider>
  );
};

export default GeneratikTable;
